<template>
  <div class="min-h-screen bg-[#F8FAFC] p-4 md:p-8 lg:p-10 font-sans text-slate-900">
    
    <header class="flex md:flex-row justify-between items-start md:items-center mb-10 gap-6 animate-fade-in-down">
      <div class="space-y-1">
        <h1 class="text-3xl md:text-4xl font-black text-slate-900 tracking-tight">
          Kiosques Agence
        </h1>
        <p class="text-slate-500 font-medium flex items-center gap-2">
          <span class="w-2 h-2 bg-indigo-500 rounded-full animate-pulse"></span>
          Gestion technique et configuration des interfaces.
        </p>
      </div>

    <div class="bg-white w-full max-w-2/4 p-4 rounded-[2rem] shadow-sm border border-slate-200/60 mb-8 flex flex-wrap gap-4 items-center animate-fade-in-down" style="animation-delay: 100ms;">
      <div class="flex-1 min-w-[300px] relative">
        <span class="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
        </span>
        <input 
          type="text" 
          v-model="search" 
          placeholder="Rechercher par ID, Nom ou Agence..." 
          class="w-full pl-12 pr-4 py-3 bg-slate-50 border-none rounded-xl focus:ring-2 focus:ring-indigo-100 focus:bg-white transition-all text-sm font-medium placeholder-slate-400"
        >
      </div>
    </div>
    </header>


    <div class="bg-white rounded-[2.5rem] shadow-xl shadow-slate-200/50 overflow-hidden border border-slate-200/60 animate-fade-in-down" style="animation-delay: 200ms;">
      
      <div v-if="loading" class="p-8 space-y-6">
        <div v-for="n in 5" :key="n" class="flex items-center justify-between animate-pulse">
           <div class="flex gap-4 w-1/3">
             <div class="h-10 w-10 bg-slate-100 rounded-xl"></div>
             <div class="space-y-2 flex-1">
               <div class="h-4 bg-slate-100 rounded w-3/4"></div>
               <div class="h-3 bg-slate-100 rounded w-1/2"></div>
             </div>
           </div>
           <div class="h-4 bg-slate-100 rounded w-24"></div>
           <div class="h-8 bg-slate-100 rounded w-32"></div>
        </div>
      </div>

      <div v-else-if="filteredKiosks.length === 0" class="p-16 text-center flex flex-col items-center justify-center">
        <div class="w-20 h-20 bg-slate-50 rounded-full flex items-center justify-center mb-4">
          <svg class="w-10 h-10 text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
        </div>
        <h3 class="text-slate-900 font-bold text-lg mb-1">Aucun kiosque trouvé</h3>
        <p class="text-slate-400 text-sm">Essayez de modifier votre recherche.</p>
      </div>

      <template v-else>
        <div class="overflow-x-auto">
          <table class="w-full border-collapse">
            <thead>
              <tr class="bg-slate-50/50 text-left text-[11px] font-black uppercase tracking-[0.15em] text-slate-400 border-b border-slate-100">
                <th class="px-8 py-6">Agence</th>
                <th class="px-8 py-6">ID / Nom</th>
                <th class="px-6 py-6 text-center">Statut</th>
                <th class="px-6 py-6">Dernière MAJ</th>
                <th class="px-8 py-6 text-right">Configuration</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-50">
              <tr v-for="kiosk in paginatedKiosks" :key="kiosk.id" class="hover:bg-slate-50/80 transition-colors group">
                
                <td class="px-8 py-6">
                  <div class="font-bold text-slate-600">{{ kiosk.agencyName }}</div>
                  <div class="text-[10px] text-slate-400 font-medium">Ref: {{ kiosk.agencyId?.substring(0,8) }}...</div>
                </td> 

                <td class="px-8 py-6">
                  <div class="flex items-center gap-3">
                    <div class="w-10 h-10 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center border border-indigo-100">
                      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-width="2" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>
                    </div>
                    <div>
                      <div class="font-black text-slate-900">{{ kiosk.name }}</div>
                      <div class="text-[10px] text-slate-400 uppercase tracking-widest font-mono">{{ kiosk.id }}</div>
                    </div>
                  </div>
                </td>

                <td class="px-6 py-6 text-center">
                  <span :class="statusClasses(kiosk.status)" class="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-wider border shadow-sm">
                    <span class="w-1.5 h-1.5 rounded-full bg-current animate-pulse"></span>
                    {{ kiosk.statusLabel }}
                  </span>
                </td>

                <td class="px-6 py-6 text-sm text-slate-500 font-medium tabular-nums">{{ kiosk.lastUpdate }}</td>
                
                <td class="px-8 py-6">
                  <div class="flex justify-end gap-2 opacity-80 group-hover:opacity-100 transition-opacity">
                    
                    <button @click="goToLayout(kiosk)" class="p-3 bg-white border border-slate-200 text-slate-400 hover:text-blue-600 hover:border-blue-200 hover:bg-blue-50 rounded-xl transition-all shadow-sm group/btn" title="Éditer Layout">
                      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" /></svg>
                    </button>

                    <button @click="goToLanguages(kiosk)" class="p-3 bg-white border border-slate-200 text-slate-400 hover:text-purple-600 hover:border-purple-200 hover:bg-purple-50 rounded-xl transition-all shadow-sm" title="Gérer les Langues">
                      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" /></svg>
                    </button>

                    <button @click="goToTicket(kiosk)" class="p-3 bg-white border border-slate-200 text-slate-400 hover:text-orange-600 hover:border-orange-200 hover:bg-orange-50 rounded-xl transition-all shadow-sm" title="Design du Ticket">
                      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" /></svg>
                    </button>

                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="px-8 py-6 border-t border-slate-100 flex items-center justify-between">
            <span class="text-xs font-bold text-slate-400 uppercase tracking-wider">
               Affichage {{ paginatedKiosks.length }} sur {{ filteredKiosks.length }}
            </span>
            <div class="flex gap-2">
                <button 
                  @click="currentPage--" 
                  :disabled="currentPage === 1"
                  class="px-4 py-2 rounded-xl border border-slate-200 text-slate-500 text-xs font-black uppercase disabled:opacity-50 hover:bg-slate-50 transition-colors"
                >
                  Précédent
                </button>
                <button 
                  @click="currentPage++" 
                  :disabled="currentPage * itemsPerPage >= filteredKiosks.length"
                  class="px-4 py-2 rounded-xl bg-indigo-50 text-indigo-600 border border-indigo-100 text-xs font-black uppercase disabled:opacity-50 hover:bg-indigo-100 transition-colors"
                >
                  Suivant
                </button>
            </div>
        </div>
      </template>
    </div>

  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { toast } from 'vue3-toastify'
import 'vue3-toastify/dist/index.css'

// Configuration
const router = useRouter()
const API_BASE = 'https://api.datafuse.fr/api/super-admin'

// State
const kiosks = ref([])
const search = ref('')
const loading = ref(true)
const currentPage = ref(1)
const itemsPerPage = ref(10)



// Computed
const filteredKiosks = computed(() => {
  if (!search.value) return kiosks.value
  const s = search.value.toLowerCase()
  return kiosks.value.filter(k => 
    k.id.toLowerCase().includes(s) || 
    k.name.toLowerCase().includes(s) ||
    (k.agencyName && k.agencyName.toLowerCase().includes(s))
  )
})

const paginatedKiosks = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage.value
  const end = start + itemsPerPage.value
  return filteredKiosks.value.slice(start, end)
})

// Watchers
watch(search, () => {
  currentPage.value = 1
})

// Actions
const fetchKiosks = async () => {
  loading.value = true
  try {
    const token = localStorage.getItem("superAdminToken")
    const res = await fetch(`${API_BASE}/kiosks`, {
      headers: { Authorization: `Bearer ${token}` }
    })
    
    if (!res.ok) throw new Error('Erreur API')

    const data = await res.json()
    
    // Simulation de délai pour voir le squelette (optionnel)
    // await new Promise(r => setTimeout(r, 800))

    kiosks.value = data.map(k => ({
      ...k,
      status: k.online ? "active" : "inactive",
      statusLabel: k.online ? "En ligne" : "Hors ligne",
      lastUpdate: new Date(k.lastUpdate).toLocaleDateString('fr-FR', { 
        day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute:'2-digit' 
      })
    }))
  } catch (err) {
    console.error(err)
    toast.error("Impossible de charger les kiosques")
  } finally {
    loading.value = false
  }
}







// Helpers
const statusClasses = (status) => {
  if (status === 'active') return 'bg-emerald-100 text-emerald-700 border-emerald-200'
  return 'bg-slate-100 text-slate-500 border-slate-200'
}

// Navigation
const goToLayout = (kiosk) => {
  router.push({ name: 'KioskLayout', params: { kioskId: kiosk.agencyId } })
}

const goToLanguages = (kiosk) => {
  router.push({ name: 'LanguageSelection', params: { kioskId: kiosk.agencyId } })
}

const goToTicket = (kiosk) => {
  router.push({ name: 'TicketDesigner', params: { kioskId: kiosk.agencyId } })
}

// Lifecycle
onMounted(() => {
  fetchKiosks()
})
</script>

<style scoped>
@keyframes slideUp {
  from { opacity: 0; transform: translateY(15px); }
  to { opacity: 1; transform: translateY(0); }
}

.animate-fade-in-down {
  animation: slideUp 0.6s cubic-bezier(0.16, 1, 0.3, 1) forwards;
  opacity: 0; /* Pour que le delay fonctionne */
}

/* Modal Animation */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

@keyframes scaleIn {
  from { opacity: 0; transform: scale(0.95); }
  to { opacity: 1; transform: scale(1); }
}

.animate-scale-in {
  animation: scaleIn 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}
</style>